import React, { useEffect, useState } from 'react';
import api from '../api';
import { Shield, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Search } from 'lucide-react';

const STATE_META = {
  'valid':     { color: '#10b981', icon: CheckCircle2, label: 'VÁLIDO' },
  'invalid':   { color: '#ef4444', icon: XCircle,      label: 'INVÁLIDO' },
  'not-found': { color: '#f59e0b', icon: AlertTriangle, label: 'NÃO ENCONTRADO' },
};

export default function RPKI() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [validity, setValidity] = useState(null);
  const [form, setForm] = useState({ asn: 'AS65000', prefix: '203.0.113.0/24' });
  const [asnResult, setAsnResult] = useState(null);
  const [asnSearch, setAsnSearch] = useState('AS65000');

  const loadStatus = async () => {
    setLoading(true); setErr('');
    try {
      const r = await api.get('/rpki/status');
      setStatus(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Routinator ainda processando dados iniciais');
    }
    setLoading(false);
  };
  useEffect(() => { loadStatus(); const i = setInterval(loadStatus, 30000); return () => clearInterval(i); }, []);

  const checkValidity = async () => {
    if (!form.asn || !form.prefix) return;
    setValidity('loading');
    try {
      const r = await api.get(`/rpki/validity?asn=${encodeURIComponent(form.asn)}&prefix=${encodeURIComponent(form.prefix)}`);
      setValidity(r.data.validated_route);
    } catch (e) { setValidity({ error: e.response?.data?.error || 'Erro' }); }
  };

  const loadAsnVrps = async () => {
    if (!asnSearch) return;
    setAsnResult('loading');
    try {
      const asnClean = asnSearch.replace(/^AS/i, '');
      const r = await api.get(`/rpki/asn/${asnClean}`);
      setAsnResult(r.data);
    } catch (e) { setAsnResult({ error: e.response?.data?.error || 'Erro' }); }
  };

  const validVrps   = status?.['valid-roas'];
  const totalVrps   = status?.['vrps'];
  const lastUpdate  = status?.['last-update-done-at'];
  const updateAgo   = status?.['last-update-done-ago'];
  const version     = status?.['version'];

  return (
    <>
      <div className="page-title">
        <h1><Shield size={20} style={{verticalAlign:'middle'}}/> RPKI · Route Origin Validation</h1>
        <button onClick={loadStatus} className="ghost" disabled={loading}>
          <RefreshCw size={14}/> {loading ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      {err && (
        <div className="err-msg" style={{padding:'.6rem 1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px'}}>
          {err} — pode levar 3-5 minutos no primeiro startup para baixar TALs dos 5 RIRs.
        </div>
      )}

      {status && (
        <div className="cards">
          <div className="card">
            <div className="label">ROAs validadas</div>
            <div className="value ok">{Number(validVrps || 0).toLocaleString('pt-BR')}</div>
          </div>
          <div className="card">
            <div className="label">VRPs totais</div>
            <div className="value">{Number(totalVrps || 0).toLocaleString('pt-BR')}</div>
          </div>
          <div className="card">
            <div className="label">Última atualização</div>
            <div className="value" style={{fontSize:'.9rem'}}>{updateAgo || '–'}</div>
          </div>
          <div className="card">
            <div className="label">Routinator</div>
            <div className="value" style={{fontSize:'.95rem'}}>{version || '–'}</div>
          </div>
        </div>
      )}

      {status && Object.keys(status).filter(k => k.startsWith('valid-roas-per-tal')).length > 0 && (
        <>
          <h3>ROAs por RIR</h3>
          <pre className="code">{status['valid-roas-per-tal']}</pre>
        </>
      )}

      {/* ── Validador de rota ───────────────────────── */}
      <h3 style={{marginTop:'2rem'}}>Validar rota (ASN + prefixo)</h3>
      <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
        Verifica se uma combinação ASN+prefixo está autorizada pelas ROAs publicadas.
      </p>
      <div className="row">
        <div>
          <label>ASN</label>
          <input value={form.asn} onChange={e => setForm({...form, asn: e.target.value})}
            placeholder="AS65000" style={{fontFamily:'JetBrains Mono'}} />
        </div>
        <div>
          <label>Prefixo</label>
          <input value={form.prefix} onChange={e => setForm({...form, prefix: e.target.value})}
            placeholder="203.0.113.0/24" style={{fontFamily:'JetBrains Mono'}} />
        </div>
        <div style={{flex:'none', paddingTop:'1.5rem'}}>
          <button onClick={checkValidity}><Search size={12}/> Validar</button>
        </div>
      </div>

      {validity === 'loading' && <p style={{color:'#a1a1aa'}}>Consultando…</p>}
      {validity?.error && <div className="err-msg">{validity.error}</div>}
      {validity?.validity && (
        <ValidityResult v={validity} />
      )}

      {/* ── ROAs por ASN ────────────────────────────── */}
      <h3 style={{marginTop:'2rem'}}>ROAs publicadas para um ASN</h3>
      <div className="row">
        <input value={asnSearch} onChange={e => setAsnSearch(e.target.value)}
          placeholder="AS65000" style={{fontFamily:'JetBrains Mono', maxWidth:'250px'}} />
        <div style={{flex:'none'}}>
          <button onClick={loadAsnVrps}><Search size={12}/> Buscar ROAs</button>
        </div>
        <div style={{flex:1}}></div>
      </div>

      {asnResult === 'loading' && <p style={{color:'#a1a1aa', marginTop:'.5rem'}}>Buscando entre 369K ROAs…</p>}
      {asnResult?.error && <div className="err-msg">{asnResult.error}</div>}
      {asnResult?.vrps && (
        <>
          <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.5rem'}}>
            {asnResult.count === 0
              ? `⚠️  Nenhuma ROA publicada para ${asnResult.asn}. Você deveria criar uma no NIC.br/LACNIC para seus prefixos.`
              : `✓ ${asnResult.count} ROA(s) válida(s) para ${asnResult.asn}:`}
          </p>
          {asnResult.count > 0 && (
            <table style={{marginTop:'.5rem'}}>
              <thead><tr><th>Prefixo</th><th>Max length</th><th>TAL</th></tr></thead>
              <tbody>
                {asnResult.vrps.map((v, i) => (
                  <tr key={i}>
                    <td style={{fontFamily:'JetBrains Mono'}}>{v.prefix}</td>
                    <td>{v.maxLength || (v.prefix.split('/')[1])}</td>
                    <td><span className="badge ok">{v.ta || '–'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <div style={{marginTop:'2rem', padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.85rem'}}>
        <strong style={{color:'#3b82f6'}}>ℹ️ O que é RPKI?</strong>
        <p style={{margin:'.5rem 0 0', color:'#a1a1aa'}}>
          RPKI (Resource Public Key Infrastructure) valida que um ASN está autorizado a anunciar um prefixo IP via BGP.
          Você cria <strong>ROAs</strong> (Route Origin Authorizations) no painel do registro do bloco (NIC.br/LACNIC) e o
          Routinator local sincroniza essas autorizações dos 5 RIRs (AFRINIC, APNIC, ARIN, LACNIC, RIPE).
        </p>
        <p style={{margin:'.5rem 0 0', color:'#a1a1aa'}}>
          Seus roteadores BGP conectam ao Routinator via <strong>RTR</strong> (Resource to Router Protocol) na porta 3323
          e usam essa informação pra rejeitar rotas inválidas (sequestros/leaks de BGP).
        </p>
        <p style={{margin:'.5rem 0 0', color:'#a1a1aa'}}>
          <strong>Conecte seus roteadores assim:</strong>
          <code style={{display:'block', marginTop:'.3rem', padding:'.4rem', background:'#0a0a0a'}}>
            router bgp 270776<br/>
            &nbsp;&nbsp;rpki server tcp 203.0.113.10 port 3323<br/>
            &nbsp;&nbsp;&nbsp;&nbsp;refresh-time 3600<br/>
            &nbsp;&nbsp;exit
          </code>
        </p>
        <p style={{margin:'.5rem 0 0', color:'#a1a1aa', fontSize:'.78rem'}}>
          (Sintaxe varia por fabricante — Cisco IOS-XR, Mikrotik RouterOS, Juniper, etc.)
        </p>
      </div>
    </>
  );
}

function ValidityResult({ v }) {
  const state = v.validity?.state || 'unknown';
  const meta = STATE_META[state] || { color: '#a1a1aa', label: state.toUpperCase() };
  const Icon = meta.icon || AlertTriangle;
  return (
    <div style={{marginTop:'.8rem', padding:'1rem', border:'1px solid ' + meta.color, borderRadius:'8px', background: 'rgba(255,255,255,.02)'}}>
      <div style={{display:'flex', alignItems:'center', gap:'.5rem', marginBottom:'.5rem'}}>
        <Icon size={20} color={meta.color}/>
        <strong style={{color: meta.color, fontSize:'1.1rem'}}>{meta.label}</strong>
      </div>
      <p style={{margin:0, color:'#a1a1aa'}}>{v.validity.description}</p>
      <p style={{margin:'.5rem 0 0', fontSize:'.85rem', fontFamily:'JetBrains Mono'}}>
        Rota: <strong>{v.route.origin_asn}</strong> → <strong>{v.route.prefix}</strong>
      </p>

      {v.validity.VRPs?.matched?.length > 0 && (
        <>
          <p style={{margin:'.5rem 0 0', fontSize:'.85rem'}}>VRPs que casam:</p>
          <pre className="code" style={{fontSize:'.78rem'}}>{JSON.stringify(v.validity.VRPs.matched, null, 2)}</pre>
        </>
      )}

      {state === 'not-found' && (
        <div style={{marginTop:'.8rem', padding:'.6rem', background:'rgba(245,158,11,.1)', borderRadius:'6px', fontSize:'.82rem'}}>
          <strong>O que fazer:</strong> Crie uma ROA no NIC.br para este ASN+prefixo.
          Acesse <a href="https://registro.br/tecnologia/recursos-de-numeracao/" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>
          registro.br/tecnologia/recursos-de-numeracao</a> → "Gerência de RPKI" → criar ROA.
        </div>
      )}
    </div>
  );
}
